import { useCallback, useEffect, useMemo } from "react";

import { useActionHook } from "hooks";
import { usePortfolioConstructionActions } from "store";
import {
  ActiveSliceType,
  DisplayModeResponse,
  DriftRow,
  DriftSubRow,
  ExposureMode,
  IObjectivesResponse,
  ObjectiveConstraintKey,
  ObjectiveRow,
  PolicyAssetClass,
  SliceGroup,
  SliceInvestor,
  TargetPolicyItem
} from "store/types";
import { toFiniteNumber } from "utils/strings";

import { acKey, buildPolicyMap, subKey } from "../Target-policy-prompt/useTargetPolicyState";
import { resolveDriftSlice, resolveSlice } from "./sliceResolution";

// The four objective constraints rendered in the panel, in display order.
export const OBJECTIVE_CONSTRAINT_KEYS: ObjectiveConstraintKey[] = [
  "target-return",
  "max-vol",
  "max-drawdown",
  "liquidity-floor"
];

const objectivesResponseToRows = (res: IObjectivesResponse): ObjectiveRow[] =>
  OBJECTIVE_CONSTRAINT_KEYS.map((key) => {
    const constraint = res.objectives?.[key];
    return {
      key,
      value: constraint?.value ?? 0,
      systemDefault: constraint?.default_value ?? 0,
      source: constraint?.source ?? "system-default",
      sourceLabel: constraint?.source_label ?? undefined,
      sourceUrl: constraint?.source_url ?? undefined,
      reviewed: constraint?.reviewed ?? false
    };
  });

// Compute one actual/target/delta set from a NAV figure pair + a target policy key.
const computeDriftFigures = (actual: number, denominator: number, targetRaw: string | undefined) => {
  const actualPercentage = denominator > 0 ? (actual / denominator) * 100 : 0;
  const hasTarget = targetRaw !== undefined && targetRaw !== null && targetRaw !== "";
  const targetPercent = hasTarget ? toFiniteNumber(targetRaw) : null;
  const target = targetPercent !== null ? (targetPercent / 100) * denominator : null;
  const delta = target !== null ? actual - target : null;
  const deltaPercentage = targetPercent !== null ? actualPercentage - targetPercent : null;
  return { actualPercentage, targetPercent, target, delta, deltaPercentage };
};

// Map the display-mode response into drift table rows. Actuals come from the
// endpoint (NAV vs NAV+unfunded+planned per the exposure toggle); targets come
// from the target policy (matched by type+id, since system/custom ids collide);
// percentages use the slice's calculated AUM denominator. Each top-level asset
// class carries its sub-asset classes as accordion children. The parent total is
// authoritative — sub-asset classes are not summed into it.
//
// `overrideAum` (when set) replaces the calculated AUM as the % denominator and
// applies in both NAV and Exposure modes — the user has explicitly pinned it.
export const buildDriftRows = (
  data: DisplayModeResponse | null,
  exposureMode: ExposureMode,
  targetPolicy: TargetPolicyItem[] | null,
  overrideAum: number | null = null
): DriftRow[] => {
  if (!data) return [];
  const isExposure = exposureMode === "exposure";
  const denominator =
    overrideAum != null
      ? overrideAum
      : isExposure
        ? toFiniteNumber(data.totals.calculated_exposure_aum)
        : toFiniteNumber(data.totals.calculated_nav_aum);
  const policyMap = buildPolicyMap(targetPolicy ?? []);

  return data.asset_class_breakdown.map((ac) => {
    const actual = isExposure ? toFiniteNumber(ac.nav_unfunded_planned) : toFiniteNumber(ac.current_nav);
    const parent = computeDriftFigures(actual, denominator, policyMap[acKey(ac.asset_class_type, ac.asset_class_id)]);

    const sub_rows: DriftSubRow[] = (ac.sub_asset_classes ?? []).map((sub) => {
      const subActual = isExposure ? toFiniteNumber(sub.nav_unfunded_planned) : toFiniteNumber(sub.current_nav);
      const subFigures = computeDriftFigures(subActual, denominator, policyMap[subKey(sub.sub_asset_class_id)]);
      return {
        sub_asset_class_id: sub.sub_asset_class_id,
        sub_asset_class_name: sub.sub_asset_class_name,
        actual_investment: subActual,
        actual_percentage: subFigures.actualPercentage,
        target: subFigures.target,
        target_percent: subFigures.targetPercent,
        delta: subFigures.delta,
        delta_percentage: subFigures.deltaPercentage
      };
    });

    return {
      asset_class_id: ac.asset_class_id,
      asset_class_name: ac.asset_class_name,
      asset_class_type: ac.asset_class_type,
      actual_investment: actual,
      actual_percentage: parent.actualPercentage,
      target: parent.target,
      target_percent: parent.targetPercent,
      delta: parent.delta,
      delta_percentage: parent.deltaPercentage,
      actual_investment_original: null,
      actual_percentage_original: null,
      opportunities: [],
      is_pinned: false,
      expected_return: null,
      expected_return_original: null,
      sub_rows
    };
  });
};

interface UseSliceDataParams {
  activeSliceType: ActiveSliceType | null;
  activeSliceKey: string | null;
  selectedInvestorId: SliceInvestor["id"] | null;
  selectedGroupId: SliceGroup["id"] | null;
}

// Owns the per-slice read figures for the portfolio-construction panels:
// objectives (+ derived AUM), the raw drift figures, and the compare/pie charts.
// Refetches whenever the active slice changes. The drift rows themselves are
// composed in the provider (they need the target policy from `useTargetPolicy`).
export const useSliceData = ({
  activeSliceType,
  activeSliceKey,
  selectedInvestorId,
  selectedGroupId
}: UseSliceDataParams) => {
  const { getObjectivesAction, getDisplayModeAction, getCompareGraphDataAction, getPieChartDataAction } =
    usePortfolioConstructionActions();

  // === Objectives (+ derived AUM) ===
  const fetchObjectives = useCallback(async (): Promise<IObjectivesResponse | null> => {
    const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return null;
    try {
      return await getObjectivesAction(slice.sliceId, slice.sliceType);
    } catch {
      return null;
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getObjectivesAction]);

  const {
    data: objectivesData = null,
    loading: isLoadingObjectives,
    action: loadObjectives,
    setData: setObjectivesData
  } = useActionHook(fetchObjectives);

  // Objectives rows are derived from the response; AUM now lives in `useAumOverride`.
  const objectives = useMemo(
    () => (objectivesData ? objectivesResponseToRows(objectivesData) : null),
    [objectivesData]
  );

  // === Drift table ===
  // Returns null when there's no active slice; errors degrade to null.
  const fetchDriftDisplay = useCallback(async (): Promise<DisplayModeResponse | null> => {
    const slice = resolveDriftSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return null;
    try {
      return await getDisplayModeAction(slice.sliceId, slice.sliceType);
    } catch {
      return null;
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getDisplayModeAction]);

  const { data: driftDisplayData, action: loadDriftTable } = useActionHook(fetchDriftDisplay);

  // Asset classes for the target-policy form come from the display-mode breakdown,
  // so they carry the system/custom type and include custom (manually-added) classes.
  const policyAssetClasses = useMemo<PolicyAssetClass[]>(
    () =>
      (driftDisplayData?.asset_class_breakdown ?? []).map((ac) => ({
        id: ac.asset_class_id,
        type: ac.asset_class_type,
        name: ac.asset_class_name,
        subClasses: (ac.sub_asset_classes ?? []).map((s) => ({
          id: s.sub_asset_class_id,
          name: s.sub_asset_class_name
        }))
      })),
    [driftDisplayData]
  );

  // === Compare graph ===
  const {
    data: compareGraph,
    loading: compareGraphLoading,
    action: getCompareGraph,
    setData: setCompareGraph
  } = useActionHook(getCompareGraphDataAction);

  const loadCompareGraph = useCallback(() => {
    const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) {
      setCompareGraph(undefined);
      return;
    }
    getCompareGraph(slice.sliceId, slice.sliceType).catch(() => setCompareGraph(undefined));
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getCompareGraph, setCompareGraph]);

  // === Pie chart ===
  const {
    data: pieChart,
    loading: pieChartLoading,
    action: getPieChart,
    setData: setPieChart
  } = useActionHook(getPieChartDataAction);

  const loadPieChart = useCallback(() => {
    const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) {
      setPieChart(undefined);
      return;
    }
    getPieChart(slice.sliceId, slice.sliceType).catch(() => setPieChart(undefined));
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getPieChart, setPieChart]);

  const reloadData = useCallback(() => {
    loadDriftTable();
    loadCompareGraph();
    loadPieChart();
  }, [loadDriftTable, loadCompareGraph, loadPieChart]);

  // Reload everything when the active slice changes.
  useEffect(() => {
    loadObjectives();
    reloadData();
  }, [activeSliceKey, loadObjectives, reloadData]);

  return {
    objectives,
    isLoadingObjectives,
    setObjectivesData,
    driftDisplayData: driftDisplayData ?? null,
    policyAssetClasses,
    reloadData,
    compareGraph,
    compareGraphLoading,
    pieChart,
    pieChartLoading
  };
};
